<script lang="ts">
	import {
		getCatalog,
		getMe,
		installCapability,
		markOnboarded,
		type CatalogEntry,
		type Me,
	} from '$lib/marketplace-api';
	import { setMe } from '$lib/auth.svelte';
	import { recommendedPickIds } from '$lib/marketplace-helpers';
	import StepWelcome from './StepWelcome.svelte';
	import StepTools from './StepTools.svelte';
	import StepSkills from './StepSkills.svelte';
	import StepFinish from './StepFinish.svelte';

	let { onFinish }: { onFinish?: () => void } = $props();

	// 4 steps: welcome → tools → skills → finish. No "skip" between steps — the
	// gate is mandatory. Zero picks in tools/skills IS allowed (the design
	// rejects forcing a pick under "no pre-selection").
	type Step = 'welcome' | 'tools' | 'skills' | 'finish';
	let step = $state<Step>('welcome');

	let catalog = $state<CatalogEntry[]>([]);
	let catalogLoading = $state(true);
	let catalogError = $state<string | null>(null);
	// isAdmin is needed to mirror the server's canInstall guard in the UI: an
	// admin-tier capability is rendered locked + non-selectable for members.
	// Default to false so a fetch failure errs on the safe side (lock admin
	// caps rather than offer them to a non-admin).
	let isAdmin = $state(false);
	let pickedTools = $state(new Set<string>()); // mcp ids
	let pickedSkills = $state(new Set<string>()); // skill ids
	let installing = $state(false);
	let installError = $state<string | null>(null);
	let done = $state(false);
	// Hold the server's post-mark /me snapshot until the user clicks a chip OR
	// the "Continue" button on the success screen. We deliberately DON'T flip
	// the layout the instant markOnboarded() returns, because that would unmount
	// the wizard before the user could see the starter-prompt chips.
	let pendingMe = $state<Me | null>(null);

	// SessionStorage handshake to seed the chat composer with a starter prompt
	// chip. routes/+page.svelte reads + clears this key on mount.
	const STARTER_PROMPT_KEY = 'sayano:starter-prompt';

	// Load the catalog + me in parallel — Tools + Skills filter the catalog;
	// the admin flag gates which panes are selectable. We don't block the
	// Welcome step on either; by the time the user clicks "Begin" both fetches
	// are almost always done. A catalog failure surfaces as a banner; a me
	// failure falls back to isAdmin=false (which over-locks rather than
	// over-offers — safer for a non-admin).
	$effect(() => {
		void (async () => {
			try {
				const [items, me] = await Promise.all([
					getCatalog(),
					getMe().catch(() => null),
				]);
				catalog = items;
				if (me) isAdmin = me.isAdmin;
				// Pre-check the recommended starter set (deselectable — the user can
				// clear any/all, and "zero is fine" still holds). recommendedPickIds
				// gates on the server's per-user `installable`, so a recommended cap the
				// viewer can't actually add (admin-only / restricted / deactivated) is
				// never pre-checked — and therefore never counted on Finish or installed
				// at "Start chatting". Seeded once, on initial load.
				pickedTools = new Set(recommendedPickIds(items, 'mcp'));
				pickedSkills = new Set(recommendedPickIds(items, 'skill'));
			} catch (e) {
				catalogError = e instanceof Error ? e.message : String(e);
			} finally {
				catalogLoading = false;
			}
		})();
	});

	function togglePick(set: Set<string>, id: string): Set<string> {
		// Svelte 5 reactivity on Set requires a new instance on mutation.
		const next = new Set(set);
		if (next.has(id)) next.delete(id);
		else next.add(id);
		return next;
	}

	function onToggleTool(id: string): void {
		pickedTools = togglePick(pickedTools, id);
	}
	function onToggleSkill(id: string): void {
		pickedSkills = togglePick(pickedSkills, id);
	}

	// Commit the wizard: flip the auth-store and run the host's onFinish hook.
	// Idempotent — safe to call multiple times in case the user clicks a chip
	// after already clicking "Continue" (though the layout will have unmounted).
	function commit(): void {
		if (!pendingMe) return;
		setMe(pendingMe);
		onFinish?.();
	}

	async function finish(): Promise<void> {
		if (installing || done) return;
		installing = true;
		installError = null;
		try {
			// Sequential install so the user sees specific failures by capability
			// id, and re-runs are safe — installCapability is idempotent on the
			// server. Catalog is small; a parallel Promise.all would shave a few
			// hundred ms at the cost of opaque error reporting.
			const ids = [...pickedTools, ...pickedSkills];
			for (const id of ids) {
				await installCapability(id);
			}
			// THEN mark onboarded — order matters: a partial install must NOT flip
			// the gate, or the user is locked into a broken state with no recourse.
			const newMe = await markOnboarded();
			pendingMe = newMe;
			done = true;
		} catch (e) {
			installError = e instanceof Error ? e.message : String(e);
		} finally {
			installing = false;
		}
	}

	function onStarterPrompt(text: string): void {
		try {
			sessionStorage.setItem(STARTER_PROMPT_KEY, text);
		} catch {
			// Storage quota / disabled — silently fall through; the user lands in
			// an empty composer rather than a broken page.
		}
		commit(); // flips the layout — the chip's text seeds the composer
	}

	// Continue without picking a chip: just flip the layout (empty composer).
	function onContinue(): void {
		commit();
	}

	// Progress indicator: 4 minimal dots + a quiet uppercase step name.
	const STEP_LABELS: Record<Step, { idx: number; label: string }> = {
		welcome: { idx: 0, label: 'Welcome' },
		tools: { idx: 1, label: 'Tools' },
		skills: { idx: 2, label: 'Skills' },
		finish: { idx: 3, label: 'Finish' },
	};
	const stepMeta = $derived(STEP_LABELS[step]);
	const STEPS: Step[] = ['welcome', 'tools', 'skills', 'finish'];
	// Pick steps go wide + full-bleed (their cards carry the glass); welcome +
	// finish sit in a narrower centered glass card.
	const isPickStep = $derived(step === 'tools' || step === 'skills');
</script>

<!-- v09 Atmospheric Dark Glass stage. Full-svh near-black canvas with faint
     aurora orbs and a subtle grain overlay; the wizard floats in a glass
     container at center. Replaces the entire app shell while the wizard is up
     — there's no way to deep-link past it because the layout branches on
     me()?.onboarded before mounting any route children. -->
<div class="relative flex min-h-svh w-full flex-col items-center justify-start overflow-hidden bg-background px-6 py-16 sm:py-20">
	<!-- Atmospheric backdrop: aurora gradients + grain. Pointer-events none so
	     it never intercepts clicks on the glass container above. -->
	<div
		class="pointer-events-none absolute inset-0 z-0"
		aria-hidden="true"
		style="background: var(--aurora-1), var(--aurora-2), var(--aurora-3), linear-gradient(180deg, var(--background) 0%, color-mix(in srgb, var(--background) 92%, white) 60%, var(--background) 100%);"
	></div>
	<!-- Two soft blurred orbs anchored top-left + bottom-right -->
	<div
		class="pointer-events-none absolute -left-32 -top-40 z-0 size-[520px] rounded-full opacity-55 blur-[80px]"
		aria-hidden="true"
		style="background: radial-gradient(circle, color-mix(in srgb, var(--primary) 24%, transparent), transparent 60%);"
	></div>
	<div
		class="pointer-events-none absolute -bottom-48 -right-40 z-0 size-[640px] rounded-full opacity-50 blur-[80px]"
		aria-hidden="true"
		style="background: radial-gradient(circle, rgba(140,100,220,0.12), transparent 60%);"
	></div>
	<!-- Faint grain overlay -->
	<div
		class="pointer-events-none absolute inset-0 z-0 mix-blend-overlay"
		aria-hidden="true"
		style="
			background-image: radial-gradient(rgba(255,255,255,0.6) 0.5px, transparent 0.5px);
			background-size: 3px 3px;
			opacity: var(--grain);
		"
	></div>

	<!-- Content column. Pick steps go wide + full-bleed; welcome/finish narrow. -->
	<div class="relative z-10 w-full {isPickStep ? 'max-w-[1080px]' : 'max-w-md'}">
		<!-- Minimal progress: 4 dots + uppercase step name. Aria progressbar for SR. -->
		<div
			class="mb-12 flex items-center justify-center gap-3 text-[10.5px] uppercase tracking-[0.18em] text-[color:var(--fg-faint)]"
			role="progressbar"
			aria-valuenow={stepMeta.idx + 1}
			aria-valuemin={1}
			aria-valuemax={4}
		>
			{#each STEPS as s, i (s)}
				{@const active = STEP_LABELS[s].idx === stepMeta.idx}
				{@const isDone = STEP_LABELS[s].idx < stepMeta.idx}
				<span
					class="inline-block rounded-full transition-all"
					style="
						width: 5px; height: 5px;
						background: {active
						? 'var(--primary)'
						: isDone
							? 'var(--muted-foreground)'
							: 'var(--glass-border-strong)'};
						box-shadow: {active ? '0 0 12px var(--glow)' : 'none'};
						transform: scale({active ? 1.4 : 1});
					"
				></span>
				{#if i < STEPS.length - 1}
					<span class="sr-only">·</span>
				{/if}
			{/each}
			<span class="ml-2 font-medium tracking-[0.12em] text-[color:var(--fg-strong)]">
				{stepMeta.label}
			</span>
		</div>

		<!-- Catalog-load failure banner (shown on the pick steps). -->
		{#if catalogError && step !== 'welcome'}
			<div
				class="mb-6 rounded-[10px] border px-3 py-2 text-[12px]"
				style="
					background: color-mix(in srgb, #f59e0b 8%, var(--glass));
					border-color: color-mix(in srgb, #f59e0b 30%, transparent);
					color: color-mix(in srgb, #f59e0b 85%, var(--fg-strong));
				"
			>
				Couldn't load the marketplace catalog: {catalogError}. You can still continue — pick from
				the marketplace later.
			</div>
		{/if}

		{#if isPickStep}
			<!-- Pick steps render full-bleed (their panes are glass). -->
			{#if step === 'tools'}
				<StepTools
					{catalog}
					{catalogLoading}
					{isAdmin}
					picked={pickedTools}
					onToggle={onToggleTool}
					onBack={() => (step = 'welcome')}
					onNext={() => (step = 'skills')}
				/>
			{:else}
				<StepSkills
					{catalog}
					{catalogLoading}
					{isAdmin}
					picked={pickedSkills}
					onToggle={onToggleSkill}
					onBack={() => (step = 'tools')}
					onNext={() => (step = 'finish')}
				/>
			{/if}
		{:else}
			<!-- Welcome + finish: centered glass card. -->
			<div
				class="rounded-[14px] border px-6 py-8 sm:px-10 sm:py-12"
				style="
					background: var(--glass);
					border-color: var(--glass-border);
					backdrop-filter: blur(18px) saturate(130%);
					-webkit-backdrop-filter: blur(18px) saturate(130%);
					box-shadow: var(--shadow-deep);
				"
			>
				{#if step === 'welcome'}
					<StepWelcome onNext={() => (step = 'tools')} />
				{:else}
					<StepFinish
						toolsCount={pickedTools.size}
						skillsCount={pickedSkills.size}
						{installing}
						{done}
						error={installError}
						onBack={() => (step = 'skills')}
						onFinish={finish}
						{onStarterPrompt}
						{onContinue}
					/>
				{/if}
			</div>
		{/if}
	</div>
</div>
